import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Pause,
  Play,
  RefreshCcw,
  Search,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  PageHeader,
} from '@eon/ui';
import { hooks, type OcppMessage } from '@eon/api-client';
import { cn, formatRelative } from '@eon/utils';

type Filter = 'all' | 'in' | 'out';

function pretty(value: unknown): string {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function MessageTypeBadge({ kind }: { kind: OcppMessage['messageType'] }) {
  const variant =
    kind === 'callerror' ? 'destructive' : kind === 'callresult' ? 'success' : 'info';
  return <Badge variant={variant}>{kind}</Badge>;
}

function MessageRow({ m }: { m: OcppMessage }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-border border-b last:border-b-0">
      <button
        type="button"
        className="hover:bg-muted/30 flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
        onClick={() => setOpen((s) => !s)}
      >
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-full',
            m.direction === 'in'
              ? 'bg-electric-400/15 text-electric-700 dark:text-electric-300'
              : 'bg-energy-500/15 text-energy-700 dark:text-energy-300',
          )}
          title={m.direction === 'in' ? 'Charger → CSMS' : 'CSMS → Charger'}
        >
          {m.direction === 'in' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />}
        </span>
        <span className="font-medium">{m.action || m.messageType}</span>
        <MessageTypeBadge kind={m.messageType} />
        {m.uid ? (
          <span className="text-muted-foreground hidden font-mono text-[10px] sm:inline">{m.uid.slice(0, 8)}</span>
        ) : null}
        <span className="text-muted-foreground ml-auto text-xs">{formatRelative(m.ts)}</span>
      </button>
      {open ? (
        <pre className="bg-muted/30 max-h-72 overflow-auto px-4 py-2 text-[11px] leading-snug">
{pretty(m.payload)}
        </pre>
      ) : null}
    </li>
  );
}

export function DeviceConsolePage() {
  const { deviceId } = useParams({ strict: false }) as { deviceId: string };
  const dq = hooks.useAdminDevice(deviceId);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [actionFilter, setActionFilter] = useState('');

  const console = hooks.useDeviceConsole(deviceId, { enabled: !paused });

  const filtered = useMemo(() => {
    return console.messages.filter((m) => {
      if (filter !== 'all' && m.direction !== filter) return false;
      if (actionFilter) {
        const needle = actionFilter.toLowerCase();
        const hay = `${m.action ?? ''} ${m.messageType}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [console.messages, filter, actionFilter]);

  const inCount = console.messages.filter((m) => m.direction === 'in').length;
  const outCount = console.messages.filter((m) => m.direction === 'out').length;

  return (
    <>
      <PageHeader
        title={`Console · ${dq.data?.displayName?.trim() || dq.data?.serialNumber || deviceId}`}
        description={
          dq.data
            ? `${dq.data.serialNumber} · ${console.connected ? 'canlı' : 'bağlanıyor…'}`
            : undefined
        }
        actions={
          <>
            <Button asChild variant="outline" size="sm">
              <Link to="/devices/$deviceId" params={{ deviceId }}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Detaya dön
              </Link>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPaused((s) => !s)}
              title={paused ? 'Yayını başlat' : 'Yayını duraklat'}
            >
              {paused ? <Play className="mr-2 h-4 w-4" /> : <Pause className="mr-2 h-4 w-4" />}
              {paused ? 'Devam et' : 'Duraklat'}
            </Button>
            <Button variant="outline" size="sm" onClick={console.clear}>
              <RefreshCcw className="mr-2 h-4 w-4" /> Temizle
            </Button>
          </>
        }
      />

      <Card className="mb-3">
        <CardContent className="flex flex-wrap items-center gap-2 p-3">
          <Badge variant={console.connected ? 'success' : 'secondary'}>
            {console.connected ? 'WS Bağlı' : 'WS Bağlı değil'}
          </Badge>
          <Badge variant="outline" className="gap-1">
            Toplam <span className="tabular-nums">{console.total}</span>
          </Badge>
          <Badge variant="info" className="gap-1">
            <ArrowDown className="h-3 w-3" /> in <span className="tabular-nums">{inCount}</span>
          </Badge>
          <Badge variant="success" className="gap-1">
            <ArrowUp className="h-3 w-3" /> out <span className="tabular-nums">{outCount}</span>
          </Badge>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="bg-muted inline-flex rounded-md p-0.5 text-xs">
              {(['all', 'in', 'out'] as Filter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded px-2 py-1 capitalize transition-colors',
                    filter === f ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {f === 'all' ? 'Tümü' : f}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
              <Input
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                placeholder="Action ara"
                className="h-8 w-36 pl-7 text-xs sm:w-44"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">OCPP Mesaj Akışı</CardTitle>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-muted-foreground py-10 text-center text-sm">
              {paused ? 'Akış duraklatıldı.' : 'Henüz mesaj yok. Cihazdan veri akmaya başladığında burada listelenir.'}
            </p>
          ) : (
            <ul className="border-border max-h-[70vh] overflow-auto rounded-md border">
              {filtered.map((m) => (
                <MessageRow key={m.id} m={m} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}
